// Port of SuiteCompile/Links.swift: what a compile does with the prose's links (`render.links`, design-docs/Links —
// design.md §4). The writers that can hold a link (EPUB, HTML, DOCX, Typst for a reading PDF, Markdown) write the
// markers they're given; plain text, narration and scripts read only the words. So this step only has to take links
// out, or turn them into words.
import { hasPrefix, type Block, type Inline } from '@writing-suite/core';
import type { CompiledSection, CompilePreset, LinkHandling } from './types.ts';

/** `CompilePipeline.linkHandling`: the preset's choice, else the format's — print interiors and scripts give the words
 *  (a page has nothing to click, and PDF/X forbids link annotations); everything else keeps links. */
export function linkHandling(preset: CompilePreset): LinkHandling {
  return preset.render.links ?? (preset.render.print !== undefined || preset.render.screenplay !== undefined ? 'text' : 'keep');
}

/** `CompilePipeline.applyingLinks(_:preset:)`: the sections with their links as the preset says. A link to another
 *  document (`fragment:`) stays only in an EPUB that keeps links (the writer points it at that document's file, or gives
 *  its words when it isn't in the book); everywhere else it's its words. */
export function applyingLinks(sections: CompiledSection[], preset: CompilePreset): CompiledSection[] {
  const handling = linkHandling(preset);
  const documentLinks = handling === 'keep' && preset.format === 'epub';
  return sections.map((section) => ({ ...section, blocks: applyingLinksToBlocks(section.blocks, handling, documentLinks) }));
}

/** Swift's `Int(_:)` of a footnote id: an optional sign and digits. */
const swiftInt = (s: string): number | null => (/^[+-]?[0-9]+$/.test(s) ? Number(s) : null);

/** `CompilePipeline.applyingLinks(_:handling:documentLinks:)`. */
export function applyingLinksToBlocks(blocks: Block[], handling: LinkHandling, documentLinks = false): Block[] {
  if (!blocks.some((b) => b.inlines.some((i) => i.type === 'linkStart'))) return blocks;
  // Notes for addresses number on from the section's own (numbered) notes; one note per address.
  const numbered = blocks.flatMap((b) => (b.kind.type === 'footnote' ? [swiftInt(b.kind.id)] : [])).filter((n): n is number => n !== null);
  let next = (numbered.length ? Math.max(...numbered) : 0) + 1;
  const noteFor = new Map<string, string>();
  const notes: Block[] = [];
  const plain = { italic: false, bold: false, smallCaps: false, underline: false, highlight: null, color: null };
  const mapped = blocks.map((block): Block => {
    const out: Inline[] = [];
    // The link being turned into words (its address), or kept as a link.
    let open: string | null = null;
    let kept = false;
    for (const inline of block.inlines) {
      if (inline.type === 'linkStart') {
        if (kept) out.push({ type: 'linkEnd' });
        const toDocument = hasPrefix(inline.address.toLowerCase(), 'fragment:');
        kept = handling === 'keep' && (!toDocument || documentLinks);
        open = kept || toDocument ? null : inline.address;
        if (kept) out.push(inline);
      } else if (inline.type === 'linkEnd') {
        if (kept) { out.push(inline); kept = false; continue; }
        if (open === null) continue;
        const address = open;
        open = null;
        if (handling === 'inline') {
          out.push({ type: 'text', text: ` (${shownAddress(address)})`, style: { ...plain } });
        } else if (handling === 'footnote') {
          let id = noteFor.get(address);
          if (id === undefined) {
            id = String(next);
            next += 1;
            noteFor.set(address, id);
            notes.push({ kind: { type: 'footnote', id }, inlines: [{ type: 'text', text: shownAddress(address), style: { ...plain } }], alignment: null });
          }
          out.push({ type: 'footnoteReference', id });
        }
      } else {
        out.push(inline);
      }
    }
    if (kept) out.push({ type: 'linkEnd' });
    return { ...block, inlines: out };
  });
  return [...mapped, ...notes];
}

/** `CompilePipeline.shown`: an address as a reader reads it — a mail link's address without `mailto:`, encoded spaces
 *  and the like decoded. */
export function shownAddress(address: string): string {
  let text: string;
  try { text = decodeURIComponent(address); } catch { text = address; }
  if (hasPrefix(text.toLowerCase(), 'mailto:')) text = text.slice(7);
  return text;
}
